import { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { TYPE_STYLE } from "../constants/opportunities";

const BLANK_ARTWORK = { title:'', medium:'', year:'', dimensions:'', price:'', description:'', imageUrl:'' };

export function OppModal({ item, onClose, onApply, currentProfile, isSaved, onToggleSave }) {
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState('details'); // 'details' | 'form' | 'payment' | 'done'
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const isPaid = item.entry_fee_type === 'paid' && item.entry_fee_amount > 0;
  const [form, setForm] = useState({
    themeResponse: '',
    artistStatement: '',
    artworks: [{ ...BLANK_ARTWORK }],
    note: '',
  });
  const type = TYPE_STYLE[item.type];

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setArtwork = (i, k, v) => setForm(f => {
    const artworks = f.artworks.map((a, idx) => idx === i ? { ...a, [k]: v } : a);
    return { ...f, artworks };
  });
  const addArtwork = () => setForm(f => ({ ...f, artworks: [...f.artworks, { ...BLANK_ARTWORK }] }));
  const removeArtwork = (i) => setForm(f => ({ ...f, artworks: f.artworks.filter((_, idx) => idx !== i) }));

  const [uploading, setUploading] = useState({}); // { [artworkIndex]: true/false }

  const handleImageUpload = async (i, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [i]: true }));
    setError(null);
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const path = `applications/${currentProfile.id}/${item.id}_${i}_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('portfolio-images')
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage
        .from('portfolio-images')
        .getPublicUrl(path);
      setArtwork(i, 'imageUrl', publicUrl);
    } catch (err) {
      setError(`Image upload failed: ${err.message}`);
    } finally {
      setUploading(u => ({ ...u, [i]: false }));
    }
  };

  const handleApplyClick = () => {
    if (!currentProfile) { setError("Please log in to apply."); return; }
    if (String(item.id).startsWith('demo-')) {
      setError("This is a sample listing — real applications open once the opportunity is posted by a gallery.");
      return;
    }
    setError(null);
    setStep('form');
  };

  const submitApplicationToSupabase = async (paymentIntentId = null) => {
    const mediums = [...new Set(form.artworks.map(a => a.medium).filter(Boolean))];
    const { error: err } = await supabase.from('applications').insert({
      opportunity_id: item.id,
      artist_id: currentProfile.id,
      theme_response: form.themeResponse || null,
      artist_statement: form.artistStatement || null,
      selected_mediums: mediums.length ? mediums : null,
      image_captions: JSON.stringify({ artworks: form.artworks, note: form.note }),
      ...(paymentIntentId ? { stripe_payment_intent_id: paymentIntentId, entry_fee_paid: true } : {}),
    });
    return err;
  };

  const handleSubmit = async () => {
    if (!form.artworks[0].title.trim()) { setError('Please add at least one artwork title.'); return; }
    if (isPaid) {
      // Advance to payment step; Stripe will handle the submit
      setError(null);
      setStep('payment');
      return;
    }
    setSubmitting(true);
    setError(null);
    const err = await submitApplicationToSupabase();
    setSubmitting(false);
    if (err) {
      if (err.code === '23505') { setStep('done'); }
      else { setError(err.message); }
    } else {
      setStep('done');
      onApply && onApply(item);
    }
  };

  const handlePayAndSubmit = async () => {
    if (!stripe || !elements) return;
    setPaymentError(null);
    setProcessingPayment(true);
    try {
      // 1. Create a PaymentIntent server-side
      const piRes = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunityId: item.id,
          artistId: currentProfile.id,
          entryFee: item.entry_fee_amount,
        }),
      });
      const piData = await piRes.json();
      if (!piRes.ok || !piData.clientSecret) {
        setPaymentError(piData.error || 'Could not initialise payment. Please try again.');
        return;
      }

      // 2. Confirm card payment
      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(piData.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (stripeErr) {
        setPaymentError(stripeErr.message || 'Payment failed — please check your card details.');
        return;
      }
      if (paymentIntent.status !== 'succeeded') {
        setPaymentError('Payment did not complete. Please try again.');
        return;
      }

      // 3. Write application (webhook also upserts as fallback)
      const err = await submitApplicationToSupabase(paymentIntent.id);
      if (err && err.code !== '23505') {
        // Payment succeeded but application write failed — show a recoverable error
        setPaymentError('Payment successful, but we could not save your application. Please contact support with reference: ' + paymentIntent.id);
        return;
      }

      setStep('done');
      onApply && onApply(item);
    } catch (err) {
      setPaymentError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const fmtPrize = v => { const n = Number(String(v||'').replace(/[,$\s]/g,'')); return v ? (isNaN(n) ? v : `$${n.toLocaleString()}`) : "—"; };
  const fmtCommission = v => (v && v !== "N/A") ? `${v}%` : "—";
  const fmtResponsibility = v => v ? `${v.charAt(0).toUpperCase()}${v.slice(1)} is responsible` : "—";

  const inp = { background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"9px 11px",fontSize:".83rem",color:C.bark,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"inherit" };
  const lbl = { fontSize:".62rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,display:"block",marginBottom:4 };

  const ModalHead = () => (
    <div className="modal-head">
      <div className="modal-handle"/>
      <button onClick={onClose} style={{ position:"absolute",top:14,right:16,background:"none",border:"none",color:C.mutedL,fontSize:"1.4rem",minHeight:44,minWidth:44,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
      <div style={{ display:"flex",gap:12,alignItems:"flex-start",marginBottom:12 }}>
        <div style={{ width:44,height:44,borderRadius:8,background:item.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.3rem",flexShrink:0 }}>{item.emoji}</div>
        <div>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.2rem",color:"#f0e2cc",fontWeight:400 }}>{item.title}</div>
          <div style={{ fontSize:".72rem",color:C.clay,fontFamily:"'DM Mono',monospace",marginTop:3 }}>{item.org} · {item.location}</div>
          {item.source === 'seeded' && (
            <span style={{ display:"inline-block",marginTop:7,fontFamily:"'DM Mono',monospace",fontSize:"11px",color:C.clay,border:`1px solid ${C.clay}`,borderRadius:20,padding:"2px 9px",letterSpacing:"1px",textTransform:"uppercase",background:"transparent" }}>
              Curated by Open Call
            </span>
          )}
        </div>
      </div>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
        <span style={{ background:type.bg,color:type.color,fontFamily:"'DM Mono',monospace",fontSize:".6rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>{type.label}</span>
        {onToggleSave && (
          <button
            onClick={onToggleSave}
            style={{ background:isSaved?C.sageL:"none",border:`1.5px solid ${isSaved?C.sage:C.mutedL}`,borderRadius:4,padding:"5px 12px",fontSize:".6rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",color:isSaved?C.sage:C.mutedL,cursor:"pointer",transition:"all .2s",minHeight:30,display:"flex",alignItems:"center",gap:5 }}
          >
            {isSaved ? "Saved ✓" : "Save opportunity"}
          </button>
        )}
      </div>
    </div>
  );

  if (step === 'done') return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box">
        <ModalHead/>
        <div className="modal-body" style={{ textAlign:"center",padding:"32px 24px" }}>
          <div style={{ fontSize:"2.5rem",marginBottom:16 }}>✓</div>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.3rem",color:C.bark,marginBottom:10 }}>Application submitted</div>
          <div style={{ fontSize:".84rem",color:C.muted,lineHeight:1.7,marginBottom:24 }}>Your application has been sent to {item.org}. You can track its status in your Applications tab.</div>
          <button className="btn-p" style={{ width:"100%",padding:"13px" }} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );

  if (step === 'payment') return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box">
        <ModalHead/>
        <div className="modal-body">
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.05rem",color:C.bark,marginBottom:6 }}>Entry fee payment</div>
          <p style={{ fontSize:".82rem",color:C.muted,lineHeight:1.65,marginBottom:20 }}>
            This open call requires a non-refundable entry fee. Your application will be submitted once payment is confirmed.
          </p>

          {/* Fee summary */}
          <div style={{ background:C.parchD,border:`1px solid ${C.sand}`,borderRadius:8,padding:"14px 16px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <span style={{ fontSize:".82rem",color:C.bark }}>Entry fee</span>
            <span style={{ fontFamily:"'DM Mono',monospace",fontSize:"1rem",color:C.bark,fontWeight:600 }}>
              ${item.entry_fee_amount.toFixed(2)} AUD
            </span>
          </div>

          {/* Card element */}
          <div style={{ marginBottom:20 }}>
            <label style={{ ...lbl, marginBottom:8 }}>Card details</label>
            <div style={{ background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"11px 13px" }}>
              <CardElement options={{ style: { base: { fontSize:"15px",color:C.bark,fontFamily:"'DM Sans',sans-serif","::placeholder":{color:C.mutedL} }, invalid:{color:C.rose} } }}/>
            </div>
          </div>

          {paymentError && (
            <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:12,fontSize:".8rem",color:C.rose }}>⚠ {paymentError}</div>
          )}

          <div style={{ display:"flex",gap:10,marginBottom:12 }}>
            <button className="btn-dk" style={{ flex:1,padding:"12px" }} onClick={()=>setStep('form')} disabled={processingPayment}>Back</button>
            <button className="btn-p" style={{ flex:2,padding:"12px" }} onClick={handlePayAndSubmit} disabled={processingPayment||!stripe}>
              {processingPayment ? "Processing…" : `Pay $${item.entry_fee_amount.toFixed(2)} & Submit →`}
            </button>
          </div>
          <p style={{ fontSize:".68rem",color:C.mutedL,textAlign:"center",margin:0 }}>🔒 Powered by Stripe. Your payment is secure.</p>
        </div>
      </div>
    </div>
  );

  if (step === 'form') return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box">
        <ModalHead/>
        <div className="modal-body">
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.05rem",color:C.bark,marginBottom:18 }}>Your application</div>

          {/* Artworks */}
          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
              <span style={{ ...lbl,marginBottom:0 }}>Artwork{form.artworks.length>1?"s":""} *</span>
              {form.artworks.length < 5 && (
                <button onClick={addArtwork} style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"3px 10px",fontSize:".68rem",color:C.muted,cursor:"pointer",fontFamily:"'DM Mono',monospace" }}>+ Add artwork</button>
              )}
            </div>
            {form.artworks.map((aw, i) => (
              <div key={i} style={{ background:C.parchD,border:`1px solid ${C.sand}`,borderRadius:8,padding:"14px",marginBottom:10 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                  <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",color:C.muted,letterSpacing:"1px",textTransform:"uppercase" }}>Artwork {i+1}</span>
                  {form.artworks.length > 1 && <button onClick={()=>removeArtwork(i)} style={{ background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:".78rem",padding:"2px 6px" }}>Remove</button>}
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={lbl}>Title *</label>
                  <input style={inp} value={aw.title} onChange={e=>setArtwork(i,'title',e.target.value)} placeholder="e.g. Untitled No. 3"/>
                </div>
                <div style={{ display:"flex",gap:10,marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Medium</label>
                    <input style={inp} value={aw.medium} onChange={e=>setArtwork(i,'medium',e.target.value)} placeholder="e.g. Oil on linen"/>
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Year</label>
                    <input style={inp} value={aw.year} onChange={e=>setArtwork(i,'year',e.target.value)} placeholder="2025"/>
                  </div>
                </div>
                <div style={{ display:"flex",gap:10,marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Dimensions</label>
                    <input style={inp} value={aw.dimensions} onChange={e=>setArtwork(i,'dimensions',e.target.value)} placeholder="e.g. 90 × 120 cm"/>
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Price (AUD)</label>
                    <input style={inp} value={aw.price} onChange={e=>setArtwork(i,'price',e.target.value)} placeholder="e.g. 1,800 or NFS"/>
                  </div>
                </div>
                <div style={{ marginBottom:0 }}>
                  <label style={lbl}>Description</label>
                  <textarea style={{ ...inp,minHeight:60,resize:"vertical" }} value={aw.description} onChange={e=>setArtwork(i,'description',e.target.value)} placeholder="Brief description of the work…"/>
                </div>
                <div style={{ marginTop:10 }}>
                  <label style={lbl}>Artwork image</label>
                  {aw.imageUrl ? (
                    <div style={{ position:"relative",display:"inline-block",width:"100%" }}>
                      <img src={aw.imageUrl} alt="Artwork" style={{ width:"100%",maxHeight:220,objectFit:"cover",borderRadius:6,display:"block" }}/>
                      <button
                        onClick={()=>setArtwork(i,'imageUrl','')}
                        style={{ position:"absolute",top:6,right:6,background:"rgba(0,0,0,.55)",border:"none",borderRadius:4,color:"#fff",fontSize:".72rem",padding:"4px 8px",cursor:"pointer" }}
                      >Remove</button>
                    </div>
                  ) : (
                    <label style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:C.parch,border:`1px dashed ${C.sand}`,borderRadius:6,padding:"16px",cursor:uploading[i]?"wait":"pointer",color:C.muted,fontSize:".8rem" }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display:"none" }}
                        onChange={e=>handleImageUpload(i, e.target.files[0])}
                        disabled={uploading[i]}
                      />
                      {uploading[i] ? "Uploading…" : "＋ Add image (JPG, PNG, WebP)"}
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Theme response */}
          {item.theme && (
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Response to theme</label>
              <div style={{ fontSize:".75rem",color:C.muted,fontStyle:"italic",marginBottom:6,lineHeight:1.5 }}>{item.theme}</div>
              <textarea style={{ ...inp,minHeight:80,resize:"vertical" }} value={form.themeResponse} onChange={e=>setField('themeResponse',e.target.value)} placeholder="How does your work relate to this theme?"/>
            </div>
          )}

          {/* Artist statement */}
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Artist statement</label>
            <textarea style={{ ...inp,minHeight:80,resize:"vertical" }} value={form.artistStatement} onChange={e=>setField('artistStatement',e.target.value)} placeholder="Brief statement about your practice…"/>
          </div>

          {/* Note to gallery */}
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Note to gallery (optional)</label>
            <textarea style={{ ...inp,minHeight:60,resize:"vertical" }} value={form.note} onChange={e=>setField('note',e.target.value)} placeholder="Anything else you'd like to add…"/>
          </div>

          {error && <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:12,fontSize:".8rem",color:C.rose }}>⚠ {error}</div>}

          <div style={{ display:"flex",gap:10 }}>
            <button className="btn-dk" style={{ flex:1,padding:"12px" }} onClick={()=>setStep('details')} disabled={submitting}>Back</button>
            <button className="btn-p" style={{ flex:2,padding:"12px" }} onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Checking…" : isPaid ? `Continue to payment ($${item.entry_fee_amount.toFixed(2)}) →` : "Submit application →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box">
        <ModalHead/>
        <div className="modal-body">
          <div style={{ background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"13px 15px",marginBottom:18,borderLeft:`3px solid ${C.clay}` }}>
            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"2px",textTransform:"uppercase",color:C.muted,marginBottom:5 }}>Theme</div>
            <div style={{ fontSize:".86rem",color:C.bark,lineHeight:1.7,fontStyle:"italic" }}>{item.theme}</div>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:18 }}>
            {[
              ["⏰ Deadline",`${item.deadline} · ${item.deadlineTime}`],
              ["🎟️ Entry fee",item.entryFee],
              ["🏆 Prize",fmtPrize(item.prize)],
              ...(item.commission!=="N/A"?[["💳 Commission",fmtCommission(item.commission)]]:[]),
              ["✅ Eligibility",item.eligibility],
              ["🇦🇺 ABN required",item.abn?"Yes":"No"],
            ].map(([k,v])=>(
              <div key={k} style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"10px 12px" }}>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                <div style={{ fontSize:".8rem",color:C.bark,fontWeight:500,lineHeight:1.4 }}>{v}</div>
              </div>
            ))}
          </div>
          {item.consignment_offered && (
            <div style={{ background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"13px 15px",marginBottom:18,borderLeft:`3px solid ${C.clay}` }}>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"2px",textTransform:"uppercase",color:C.muted,marginBottom:10 }}>Consignment terms</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:9 }}>
                {[
                  ["📅 Duration", item.consignment_duration ? `${item.consignment_duration} weeks` : "—"],
                  ["💳 Commission", fmtCommission(item.commission)],
                  ["🛡️ Damage policy", fmtResponsibility(item.damage_policy)],
                  ["📦 Return shipping", fmtResponsibility(item.return_shipping)],
                  ...(item.payment_terms ? [["💰 Payment terms", `${item.payment_terms} days`]] : []),
                ].map(([k,v])=>(
                  <div key={k} style={{ background:C.cream,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"10px 12px" }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                    <div style={{ fontSize:".8rem",color:C.bark,fontWeight:500,lineHeight:1.4 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && (
            <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:12,fontSize:".8rem",color:C.rose }}>
              ⚠ {error}
            </div>
          )}
          {item.external_url ? (
            <>
              <a
                href={item.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-p"
                style={{ display:"flex",alignItems:"center",justifyContent:"center",width:"100%",padding:"13px",textDecoration:"none",boxSizing:"border-box" }}
              >
                Apply on {item.org}'s website ↗
              </a>
              <p style={{ fontSize:".72rem",color:C.muted,lineHeight:1.55,margin:"10px 0 0",textAlign:"center" }}>
                This opportunity is managed externally. You'll be taken to their website to apply.
              </p>
            </>
          ) : (
            <button className="btn-p" style={{ width:"100%",padding:"13px" }} onClick={handleApplyClick}>
              Apply Now →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
